import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { isPushSupported, getPermissionState, isSubscribed, subscribeToPush } from '../utils/push';

/**
 * رسالة صغيرة تظهر مرة وحدة تسأل المستخدم "هل تريد تفعيل الإشعارات؟"
 * تظهر بعد تسجيل الدخول بشوي، وما تكرر نفسها لو رفض أو وافق أو فعّلها من مكان ثاني.
 *
 * apiClient: عميل axios المطلوب استخدامه (افتراضياً `api` العادي؛ مرّر
 * distributorApi عند استخدامه بلوحة الموزع — راجع utils/push.js).
 * dismissKey: مفتاح localStorage لتذكّر "لاحقاً" — منفصل عن المستخدم العادي
 * حتى لا يشترك الجهاز نفسه (لو استُخدم لحساب مستخدم وموزع معاً) بنفس حالة
 * الرفض/القبول بينهما.
 */
export default function PushPermissionPrompt({ apiClient, dismissKey = 'push_prompt_dismissed_at' }) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function check() {
      if (!isPushSupported()) return;

      const state = getPermissionState();

      // ⚠️ إصلاح: المستخدم اللي وافق على الإشعارات مسبقاً (granted) ما كان
      // يُعاد تسجيل اشتراكه بالسيرفر أبداً بعد أول مرة — لأن هذا الشرط أدناه
      // (=== 'default') يمنع الكود من الوصول لـ subscribeToPush() إطلاقاً
      // له. بيانات كل شبكة أصبحت الآن بقاعدة بيانات مستقلة (تعدد الشبكات)،
      // فأي مستخدم كانت بياناته بقاعدة قديمة أو انتقل بين شبكات يفقد سجل
      // اشتراكه بالسيرفر الجديد بصمت رغم بقاء الإذن "مفعّل" بمتصفحه. الحل:
      // نُعيد المزامنة بصمت (بدون أي واجهة تظهر له) في كل مرة يفتح التطبيق —
      // آمنة تماماً لأن الراوت upsert بطبيعته.
      if (state === 'granted') {
        subscribeToPush(apiClient).catch(() => {});
        return;
      }

      if (state !== 'default') return; // 'denied' أو غير مدعوم — لا شيء نقدر نسويه

      const dismissedAt = localStorage.getItem(dismissKey);
      if (dismissedAt && Date.now() - Number(dismissedAt) < 14 * 24 * 60 * 60 * 1000) return;

      const already = await isSubscribed();
      if (already || cancelled) return;

      setVisible(true);
    }

    const timer = setTimeout(check, 2500); // تأخير بسيط حتى ما تظهر فوراً مع دخول الصفحة
    return () => { cancelled = true; clearTimeout(timer); };
  }, [apiClient, dismissKey]);

  const handleEnable = async () => {
    setVisible(false);
    const ok = await subscribeToPush(apiClient);
    if (ok) toast.success('🔔 تم تفعيل الإشعارات بنجاح');
    else toast.error('لم نتمكن من تفعيل الإشعارات');
  };

  const handleDismiss = () => {
    setVisible(false);
    localStorage.setItem(dismissKey, String(Date.now()));
  };

  if (!visible) return null;

  return (
    <div style={overlayStyle}>
      <div style={cardStyle}>
        <div style={{ fontSize: 32 }}>🔔</div>
        <div style={{ flex: 1 }}>
          <p style={{ margin: 0, fontWeight: 700, fontSize: 15 }}>
            تفعيل الإشعارات؟
          </p>
          <p style={{ margin: '4px 0 0', fontSize: 13, opacity: 0.75 }}>
            نوصلك بحالة طلباتك فوراً، حتى لو التطبيق مقفول
          </p>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <button onClick={handleEnable} style={enableBtnStyle}>تفعيل</button>
          <button onClick={handleDismiss} style={laterBtnStyle}>لاحقاً</button>
        </div>
      </div>
    </div>
  );
}

const overlayStyle = {
  position: 'fixed',
  bottom: 0,
  left: 0,
  right: 0,
  zIndex: 9998,
  display: 'flex',
  justifyContent: 'center',
  padding: '12px',
  pointerEvents: 'none',
};

const cardStyle = {
  pointerEvents: 'auto',
  width: '100%',
  maxWidth: 420,
  background: '#ffffff',
  borderRadius: 16,
  boxShadow: '0 8px 30px rgba(0,0,0,0.18)',
  padding: '14px 16px',
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  direction: 'rtl',
  fontFamily: 'Tajawal, sans-serif',
};

const enableBtnStyle = {
  background: '#0f4c81',
  color: '#fff',
  border: 'none',
  borderRadius: 10,
  padding: '8px 16px',
  fontWeight: 700,
  fontSize: 13,
  cursor: 'pointer',
};

const laterBtnStyle = {
  background: 'transparent',
  color: '#666',
  border: 'none',
  borderRadius: 10,
  padding: '4px 8px',
  fontSize: 12,
  cursor: 'pointer',
};
